import Navbar from "../Navbar";
import { HexColorPicker } from "react-colorful";
import { useContext, useEffect, useState } from "react";
import SettingsContext from "../../contexts/SettingsContext";
import UserContext from "../../contexts/UserContext";
import { Link } from "react-router-dom";
import { auth } from "../../utils/firebase";
import { signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import {
  CollectionReference,
  DocumentData,
  collection,
  getDocs,
  deleteDoc,
  Query,
  query,
  where,
  QuerySnapshot,
  doc,
} from "firebase/firestore";

/**
 * Renders the "/settings" page
 * @returns html containing all of the settings
 */
const Settings = () => {
  useEffect(() => {
    document.title = "Settings - FPS Aim Trainer";
  }, []);

  const settingsContext = useContext(SettingsContext);
  const userContext = useContext(UserContext);
  const navigate = useNavigate(); // navigate hook from react router to navigate pages
  const [selectedGrouping, setSelectedGrouping] = useState("General"); // selected settings grouping

  /**
   * Logout of current account
   */
  const logout = async () => {
    await signOut(auth);
    navigate("/");
  };

  /**
   * Go to user data page
   */
  const getUserData = () => {
    navigate("/user-data");
  };

  /**
   * A function to delete all data tied to a user from the Firestore database and set the
   * userAccountData, userSettingsData, and userRoundsData useState variables back to empty.
   */
  const deleteData = async () => {
    const db = userContext.db;
    if (!db) return;

    const accountsRef: CollectionReference<DocumentData> = collection(
      db,
      "users"
    );

    const qAccounts: Query<DocumentData> = query(
      accountsRef,
      where("uID", "==", userContext.user?.uid)
    );

    const querySnapshotAccounts: QuerySnapshot<DocumentData> = await getDocs(
      qAccounts
    );

    querySnapshotAccounts.forEach((doc) => {
      deleteDoc(doc.ref);
    });

    //********************************************************** */

    const settingsRef: CollectionReference<DocumentData> = collection(
      db,
      "settings"
    );

    deleteDoc(doc(settingsRef, userContext.user?.uid));

    //********************************************************** */

    const roundsRef: CollectionReference<DocumentData> = collection(
      db,
      "rounds"
    );

    const qRounds: Query<DocumentData> = query(
      roundsRef,
      where("userID", "==", userContext.user?.uid)
    );

    const querySnapshotRounds: QuerySnapshot<DocumentData> = await getDocs(
      qRounds
    );

    querySnapshotRounds.forEach((doc) => {
      deleteDoc(doc.ref);
    });

    await logout(); // logout of account
  };

  // different settings options
  const groupings = ["General", "Iron Sights", "Duels"];

  // the color options
  let colors = [
    {
      name: "Background Color",
      value: settingsContext.backgroundColor,
      changeValue: settingsContext.logAndSetBackgroundColor,
      description:
        "This color will be used as the background color for the aim trainer in both Duels and Iron Sights.",
    },
    {
      name: "Projectile Color",
      value: settingsContext.projectileColor,
      changeValue: settingsContext.logAndSetProjectileColor,
      description:
        "This color will be used as the projectiles' color in both Duels and Iron Sights.",
    },
  ];

  // buttons in general settings availbale if the user is logged in
  const userButtons = [
    {
      title: "Retreive Stored Data",
      description:
        "Retreive all of the data that this aim trainer has stored about you.",
      button: "Get Data",
      buttonClick: getUserData,
    },
    {
      title: "Delete Stored Data",
      description:
        "Delete all of the data that this aim trainer has stored about you.",
      button: "Delete Data",
      buttonClick: deleteData,
    },
  ];

  // the iron sights options
  const ironSightsSettings = [
    {
      name: "Target Velocity",
      value: settingsContext.targetVel,
      changeValue: settingsContext.logAndSetTargetVel,
      description:
        "This value sets the velocity of the moving targets in Iron Sights.",
      min: 5,
      max: 100,
      multiplier: 1 / 100,
    },
    {
      name: "Target Acceleration",
      value: settingsContext.targetAcc,
      changeValue: settingsContext.logAndSetTargetAcc,
      description:
        "This value sets the acceleration of the moving targets in Iron Sights.",
      min: -10,
      max: -1,
      multiplier: 0.002,
    },
  ];

  // the duels options
  const duelsSettings = [
    {
      name: "Target Duration",
      value: settingsContext.duration,
      changeValue: settingsContext.logAndSetDuration,
      description:
        "This value sets the amount of time for the static target to be on the screen in Duels.",
      min: 0,
      max: 10,
      multiplier: 1000,
    },
  ];

  /**
   * Renders an iron sights or duels setting that uses validation
   * @param name of the setting
   * @param value is the true value of the setting
   * @param changeValue changes the true value of the setting
   * @param description is the description of the setting
   * @param min is the minimum value of the setting
   * @param max is the maximum value of the setting
   * @param multiplier is the multiplier to convert the showed value to the actual value
   * (i.e. rather than showing the user a setting of 0.0004)
   * @returns an input the user can change w validation
   */
  const renderSetting = (
    name: string,
    value: number | undefined,
    changeValue: ((num: number) => void) | undefined,
    description: string,
    min: number,
    max: number,
    multiplier: number
  ) => (
    <div className="flex flex-col md:flex-row items-center gap-4" key={name}>
      <div>
        <p className="text-lg font-semibold">{name}</p>
        <p className="text-md max-w-xs">{description}</p>
      </div>
      <div className="relative">
        <input
          id={name}
          type="number"
          className="focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md w-20 px-2 py-1"
          value={validation[name]}
          min={min}
          max={max}
          onChange={(e) => {
            let val: string | number = parseFloat(e.target.value); // gets the value
            if (!Number.isFinite(val)) val = ""; // set value to empty string if value is NaN
            if (
              changeValue &&
              typeof val === "number" &&
              val >= min &&
              val <= max
            ) {
              changeValue(val * multiplier); // if value is valid change the true value
            }
            setValidation((cur) => {
              // set the displayed value
              cur = { ...cur };
              cur[name] = val;
              return cur;
            });
          }}
        />
        {
          /** if the value is invalid then let the user know **/
          (validation[name] < min ||
            validation[name] > max ||
            typeof validation[name] !== "number") && (
            <label
              htmlFor={name}
              className="absolute top-10 w-48 left-0 text-red-500 font-semibold"
            >
              This setting must be inbetween {min} and {max}.
            </label>
          )
        }
      </div>
    </div>
  );

  // temp states for iron sights and duels settings (to show user if input is valid or not without
  // storing that invalid data in place of valid data)
  const [validation, setValidation] = useState<{
    [key: string]: any;
  }>({});

  // set the validation at the beginning
  useEffect(() => {
    let out: {
      [key: string]: any;
    } = {};
    ironSightsSettings.forEach((setting) => {
      out[setting.name] = setting.value
        ? setting.value / setting.multiplier
        : "";
    });
    duelsSettings.forEach((setting) => {
      out[setting.name] = setting.value
        ? setting.value / setting.multiplier
        : "";
    });
    setValidation(out);
  }, []);

  /**
   * Adds a color to target colors
   */
  const clickAddColor = () => {
    if (settingsContext.logAndSetTargetColors) {
      settingsContext.logAndSetTargetColors((cur: string[]) => {
        cur = JSON.parse(JSON.stringify(cur));
        cur.push("#ef4444");
        return cur;
      });
    }
  };

  /**
   * Removes a color from target colors
   */
  const clickRemoveColor = () => {
    if (settingsContext.logAndSetTargetColors) {
      settingsContext.logAndSetTargetColors((cur: string[]) =>
        cur.slice(0, cur.length - 1)
      );
    }
  };

  return (
    <div className="h-screen flex flex-col">
      <Navbar />
      <div className="w-screen flex-1 flex overflow-hidden">
        <div className="flex flex-col w-36 sm:w-52 lg:w-64 h-full py-8 bg-white border-r">
          <h2 className="text-3xl font-semibold text-center text-indigo-500 mb-8">
            Settings
          </h2>
          <div className="flex flex-col flex-1 gap-y-8">
            {groupings.map((grouping) => (
              <button
                className={
                  "flex items-center pl-8 pr-2 py-2 text-gray-700 font-medium hover:bg-gray-100 " +
                  (selectedGrouping === grouping ? "bg-gray-100" : "")
                }
                onClick={() => setSelectedGrouping(grouping)}
                key={grouping}
              >
                {grouping}
              </button>
            ))}
          </div>
          <div className="flex-col items-center hidden sm:flex">
            <Link to="/iron-sights">
              <button className="outline outline-red-500 outline-2 hover:bg-red-500 text-md rounded-md text-center w-40 py-2 mb-4">
                Play Iron Sights
              </button>
            </Link>
            <Link to="/duels">
              <button className="outline outline-red-500 outline-2 hover:bg-red-500 text-md rounded-md text-center w-40 py-2 mb-4">
                Play Duels
              </button>
            </Link>
          </div>
        </div>
        <div className="flex-1 bg-gray-100 px-4 overflow-y-scroll">
          {selectedGrouping === "General" ? (
            <div className="flex flex-col pt-8 pb-4 items-center gap-8">
              <h2 className="text-3xl font-semibold text-indigo-500 mb-4 hidden sm:block">
                General Settings
              </h2>
              {colors.map((color) => (
                <div
                  className="flex flex-col md:flex-row items-center gap-4"
                  key={color.name}
                >
                  <div>
                    <p className="text-lg font-semibold">{color.name}</p>
                    <p className="text-md max-w-xs">{color.description}</p>
                  </div>
                  <HexColorPicker
                    color={color.value}
                    onChange={color.changeValue}
                  />
                </div>
              ))}
              <div className="flex flex-col md:flex-row gap-4">
                <div className="pt-12">
                  <p className="text-lg font-semibold">Target Colors</p>
                  <p className="text-md max-w-xs">
                    The target's colors will be randomized from the following
                    colors:
                  </p>
                </div>
                <div className="flex flex-col gap-4 items-center">
                  {settingsContext.targetColors?.map((color, i) => (
                    <HexColorPicker
                      key={i}
                      color={color}
                      onChange={(newColor) => {
                        if (settingsContext.logAndSetTargetColors)
                          settingsContext.logAndSetTargetColors(
                            (cur: string[]) => {
                              cur = JSON.parse(JSON.stringify(cur));
                              cur[i] = newColor;
                              return cur;
                            }
                          );
                      }}
                    />
                  ))}
                  <button
                    type="button"
                    className="bg-indigo-500 py-1 px-3 rounded-lg text-white hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white"
                    onClick={clickAddColor}
                  >
                    Add Color
                  </button>
                  {settingsContext.targetColors &&
                    settingsContext.targetColors?.length > 1 && (
                      <button
                        type="button"
                        className="bg-indigo-500 py-1 px-3 rounded-lg text-white hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white"
                        onClick={clickRemoveColor}
                      >
                        Remove Color
                      </button>
                    )}
                </div>
              </div>
              {userContext.loggedIn && (
                <>
                  {userButtons.map((item) => (
                    <div
                      className="flex flex-col md:flex-row items-center gap-4"
                      key={item.title}
                    >
                      <div>
                        <p className="text-lg font-semibold">{item.title}</p>
                        <p className="text-md max-w-xs">{item.description}</p>
                      </div>
                      <div
                        style={{ width: "200px" }}
                        className="flex justify-center items-center"
                      >
                        <button
                          className="mt-2 px-6 py-2 text-sm font-semibold text-indigo-900 hover:bg-indigo-100 rounded-sm"
                          onClick={item.buttonClick}
                        >
                          {item.button}
                        </button>
                      </div>
                    </div>
                  ))}

                  <button
                    className="mt-4 mb-4 px-6 py-2 text-sm font-semibold text-indigo-800 bg-indigo-100 hover:bg-indigo-900 hover:text-white rounded-sm"
                    onClick={logout}
                  >
                    Sign Out
                  </button>
                </>
              )}
            </div>
          ) : selectedGrouping === "Iron Sights" ? (
            <div className="flex flex-col pt-8 pb-4 items-center gap-8">
              <h2 className="text-3xl font-semibold text-indigo-500 mb-4 hidden sm:block">
                Iron Sights Settings
              </h2>
              {ironSightsSettings.map((setting) =>
                renderSetting(
                  setting.name,
                  setting.value,
                  setting.changeValue,
                  setting.description,
                  setting.min,
                  setting.max,
                  setting.multiplier
                )
              )}
            </div>
          ) : (
            <div className="flex flex-col pt-8 pb-4 items-center gap-8">
              <h2 className="text-3xl font-semibold text-indigo-500 mb-4 hidden sm:block">
                Duels Settings
              </h2>
              {duelsSettings.map((setting) =>
                renderSetting(
                  setting.name,
                  setting.value,
                  setting.changeValue,
                  setting.description,
                  setting.min,
                  setting.max,
                  setting.multiplier
                )
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
